import React, { useState, useRef, useEffect } from "react";
import { cva } from "class-variance-authority";
import clsx from "clsx";

// PopOver 定位样式（click / hover 模式）
const popoverStyles = cva(
  "absolute z-50 opacity-0 transition-all duration-[200ms] ease-out pointer-events-none",
  {
    variants: {
      anchor: {
        "top-right": "top-full right-0 mt-2",
        "top-left": "top-full left-0 mt-2",
        middle: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      },
      visible: {
        true: "opacity-100 translate-y-1 duration-[200ms] ease-in pointer-events-auto",
        false: ""
      }
    },
    defaultVariants: {
      anchor: "top-right",
      visible: false
    }
  }
);

// group-hover 浮层定位（不含可见性，由 JS state 控制）
const groupHoverPanelAnchor = {
  "top-right": "top-full right-0",
  "top-left": "top-full left-0",
  middle: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
} as const;

interface PopOverProps {
  trigger?: "hover" | "click" | "group-hover";
  children: React.ReactNode;
  content: React.ReactNode;
  anchor?: "top-right" | "top-left" | "middle";
  className?: string;
  containerClassName?: string;
}

const HIDE_DELAY = 120;

const PopOver: React.FC<PopOverProps> = ({
  trigger = "hover",
  children,
  content,
  anchor = "top-right",
  className,
  containerClassName
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // group-hover 模式的可见状态（JS 控制，解决 CSS pointer-events 死区问题）
  const [groupVisible, setGroupVisible] = useState(false);
  const groupVisibleRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (groupTimeoutRef.current) clearTimeout(groupTimeoutRef.current);
    };
  }, []);

  // ── Click 外部关闭 ──────────────────────────────────────────────
  useEffect(() => {
    if (trigger !== "click") return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    };
    if (isVisible) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isVisible, trigger]);

  // ── group-hover 模式：JS 延迟 hover（消除 CSS pointer-events 死区）──
  const handleGroupEnter = () => {
    if (groupTimeoutRef.current) clearTimeout(groupTimeoutRef.current);
    groupVisibleRef.current = true;
    setGroupVisible(true);
  };

  const handleGroupLeave = () => {
    groupVisibleRef.current = false;
    groupTimeoutRef.current = setTimeout(() => {
      if (!groupVisibleRef.current) {
        setGroupVisible(false);
      }
    }, HIDE_DELAY);
  };

  // ── hover 模式 ─────────────────────────────────────────────────
  const isHoveredRef = useRef(false);

  const handleMouseEnter = () => {
    if (trigger !== "hover") return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    isHoveredRef.current = true;
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (trigger !== "hover") return;
    isHoveredRef.current = false;
    timeoutRef.current = setTimeout(() => {
      if (!isHoveredRef.current) {
        setIsVisible(false);
      }
    }, HIDE_DELAY);
  };

  const handlePopoverMouseEnter = () => {
    if (trigger !== "hover") return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    isHoveredRef.current = true;
  };

  const handlePopoverMouseLeave = () => {
    if (trigger !== "hover") return;
    isHoveredRef.current = false;
    timeoutRef.current = setTimeout(() => {
      if (!isHoveredRef.current) {
        setIsVisible(false);
      }
    }, HIDE_DELAY);
  };

  // ── click 模式 ─────────────────────────────────────────────────
  const handleClick = () => {
    if (trigger !== "click") return;
    setIsVisible((v) => !v);
  };

  const triggerProps =
    trigger === "hover"
      ? { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }
      : { onClick: handleClick };

  const popoverProps =
    trigger === "hover"
      ? { onMouseEnter: handlePopoverMouseEnter, onMouseLeave: handlePopoverMouseLeave }
      : {};

  // ── group-hover 渲染（JS state 控制可见性，保留 group class 供子元素使用）──
  if (trigger === "group-hover") {
    return (
      <div
        ref={containerRef}
        className={clsx("relative group", containerClassName)}
        onMouseEnter={handleGroupEnter}
        onMouseLeave={handleGroupLeave}
      >
        {children}
        <div
          className={clsx(
            "absolute z-50 origin-top-right",
            groupHoverPanelAnchor[anchor],
            "transition-all duration-[200ms] ease-in-out",
            groupVisible
              ? "opacity-100 translate-y-1 visible pointer-events-auto"
              : "opacity-0 invisible pointer-events-none",
            className
          )}
          onMouseEnter={handleGroupEnter}
          onMouseLeave={handleGroupLeave}
        >
          {content}
        </div>
      </div>
    );
  }

  // ── hover / click 渲染 ─────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={clsx("relative", "[&>div:nth-child(1)]:h-full", containerClassName)}
      {...triggerProps}
    >
      {children}
      <div
        className={clsx(popoverStyles({ anchor, visible: isVisible }), className)}
        {...popoverProps}
      >
        {content}
      </div>
    </div>
  );
};

export default PopOver;
